import React, { useState, useMemo } from 'react';
import { Select, MenuItem, FormControl, InputLabel, Box, Card, Typography, ToggleButtonGroup, ToggleButton, Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { it } from 'date-fns/locale';

const MONTH_OPTIONS = ['3', '6', '12', '18'];

function SelectExpDate(props) {
    const {
        onExpDateChange,
        expirationMonthDuration,
        expirationDate,
    } = props;

    const [mode, setMode] = useState('months');
    const [customDate, setCustomDate] = useState(null);

    const formatDate = (isoDate) => {
        const date = new Date(isoDate);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('it-IT', options);
    };

    // La scadenza non può essere nel passato: minimo selezionabile è domani.
    const minDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const handleModeChange = (event, newMode) => {
        // ToggleButtonGroup esclusivo: ignora il "deseleziona" (newMode === null).
        if (newMode === null) {
            return;
        }
        setMode(newMode);
        if (newMode === 'months') {
            setCustomDate(null);
            // se non è selezionata una durata valida, torna al default di 18 mesi
            if (onExpDateChange && !MONTH_OPTIONS.includes(expirationMonthDuration)) {
                onExpDateChange('18');
            }
        }
    };

    const handleSelectChange = (event) => {
        if (onExpDateChange) {
            onExpDateChange(event.target.value);
        }
    };

    const handleDateChange = (newValue) => {
        setCustomDate(newValue);
        // notifica il parent solo quando la data è valida e completa
        if (newValue instanceof Date && !isNaN(newValue.getTime()) && onExpDateChange) {
            onExpDateChange(newValue);
        }
    };

    // Se il valore upstream non è una durata valida (es. data personalizzata),
    // mostra comunque il default di 18 mesi nel selettore dei mesi.
    const selectValue = MONTH_OPTIONS.includes(expirationMonthDuration) ? expirationMonthDuration : '18';

    return (
        <Card sx={{ padding: 3 }}>
            <Stack spacing={2.5}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Durata del laboratorio virtuale
                </Typography>

                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    color="primary"
                    size="small"
                    onChange={handleModeChange}
                >
                    <ToggleButton value="months">Durata in mesi</ToggleButton>
                    <ToggleButton value="custom">Data personalizzata</ToggleButton>
                </ToggleButtonGroup>

                {mode === 'months' ? (
                    <FormControl fullWidth>
                        <InputLabel id="exp-date-label">Durata</InputLabel>
                        <Select
                            labelId="exp-date-label"
                            label="Durata"
                            value={selectValue}
                            onChange={handleSelectChange}
                        >
                            <MenuItem value="18">18 mesi</MenuItem>
                            <MenuItem value="12">12 mesi</MenuItem>
                            <MenuItem value="6">6 mesi</MenuItem>
                            <MenuItem value="3">3 mesi</MenuItem>
                        </Select>
                    </FormControl>
                ) : (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                        <DatePicker
                            label="Data di scadenza"
                            value={customDate}
                            onChange={handleDateChange}
                            minDate={minDate}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    helperText: 'Seleziona la data di scadenza del laboratorio',
                                },
                            }}
                        />
                    </LocalizationProvider>
                )}

                {expirationDate && (
                    <Box
                        sx={{
                            backgroundColor: 'action.hover',
                            borderRadius: 1,
                            padding: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Data di scadenza
                        </Typography>
                        <Typography variant="h6" component="div">
                            {formatDate(expirationDate)}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Card>
    );
}

export default SelectExpDate;
